import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Urls, { postRequest } from '../../services/common/urls';
import './UpdateNotification.css';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_STORAGE_KEY = 'app_version';

function UpdateNotification() {
  const [showNotification, setShowNotification] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Get current version from build-time meta tag or use timestamp
    const getCurrentVersion = () => {
      const metaVersion = document.querySelector('meta[name="app-version"]')?.content;
      if (metaVersion) {
        return metaVersion;
      }
      // Fallback: use build timestamp from package.json or current time
      return new Date().getTime().toString();
    };

    const currentVersion = getCurrentVersion();

    // Store initial version if not exists
    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
    if (!storedVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
    }

    // Check for updates periodically
    const checkForUpdates = () => {
      setIsChecking(true);
      postRequest(Urls.GET_APP_VERSION, {}, (res) => {
        setIsChecking(false);
        if (res && res.status === true && res.data) {
          const serverVersion = res.data.version;
          const localVersion = localStorage.getItem(VERSION_STORAGE_KEY);

          if (serverVersion && serverVersion !== localVersion) {
            setShowNotification(true);
          }
        }
      });
    };

    // Check immediately on mount (after a short delay to avoid blocking initial render)
    const initialCheckTimeout = setTimeout(checkForUpdates, 2000);

    // Set up periodic checking
    const interval = setInterval(checkForUpdates, VERSION_CHECK_INTERVAL);

    return () => {
      clearTimeout(initialCheckTimeout);
      clearInterval(interval);
    };
  }, []);

  const handleRefresh = () => {
    // Update stored version to server version before refresh
    postRequest(Urls.GET_APP_VERSION, {}, (res) => {
      if (res && res.status === true && res.data) {
        localStorage.setItem(VERSION_STORAGE_KEY, res.data.version);
      }
      // Force reload from server (bypass cache)
      window.location.reload(true);
    });
  };

  const handleDismiss = () => {
    // Update stored version to current server version (user dismissed, will check again later)
    postRequest(Urls.GET_APP_VERSION, {}, (res) => {
      if (res && res.status === true && res.data) {
        localStorage.setItem(VERSION_STORAGE_KEY, res.data.version);
      }
    });
    setShowNotification(false);
  };

  if (!showNotification) {
    return null;
  }

  return (
    <div className="update-notification">
      <div className="update-notification-content">
        <div className="update-notification-message">
          <img 
            src="/favicon-32x32.png" 
            alt="App Icon" 
            className="update-notification-favicon"
            onError={(e) => {
              // Fallback to favicon.ico if 32x32 doesn't exist
              e.target.src = '/favicon.ico';
            }}
          />
          <ReloadOutlined className="update-notification-icon" />
          <span>A new version is available!</span>
        </div>
        <div className="update-notification-actions">
          <Button 
            type="link" 
            size="small" 
            onClick={handleDismiss}
            className="update-notification-dismiss"
          >
            Later
          </Button>
          <Button 
            type="primary" 
            size="small" 
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            className="update-notification-refresh"
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UpdateNotification;

